import chroma from "chroma-js";
import { useMemo } from "react";
import { useSocietySession } from "shared/context/SocietySessionContext";

interface ColorComponents {
  h?: number;
  s?: number;
  l?: number;
}

interface SocietyColorPalette {
  paletteByName: Record<string, string>;
  paletteList: string[];
}

const TARGET_DYNAMIC_COLOR_COUNT = 20;
const MAX_LIGHTNESS = 0.72;

export const hslToHex = (h: number, s: number, l: number) =>
  chroma
    .hsl(h, s / 100, l / 100)
    .hex()
    .toUpperCase();

/**
 * Pulls color HSL parts out of the society theme variables.
 *
 * Example expected properties:
 * - color-society-hue
 * - color-society-saturation
 * - color-society-lightness
 */
const getColorComponentsFromTheme = (
  theme: Array<{ property: string; value: unknown }>,
): Record<string, ColorComponents> => {
  const components: Record<string, ColorComponents> = {};

  theme.forEach(({ property, value }) => {
    // Ignore non-color values and light variants.
    if (!property.startsWith("color-") || property.includes("-light-")) {
      return;
    }

    const addPart = (key: string, part: keyof ColorComponents) => {
      const numeric = parseFloat(String(value).replace("%", ""));

      if (!Number.isFinite(numeric)) {
        return;
      }

      components[key] = components[key] ?? {};
      components[key][part] = numeric;
    };

    if (property.endsWith("-hue")) {
      const key = property.slice("color-".length, -"-hue".length);
      addPart(key, "h");
    } else if (property.endsWith("-saturation")) {
      const key = property.slice("color-".length, -"-saturation".length);
      addPart(key, "s");
    } else if (property.endsWith("-lightness")) {
      const key = property.slice("color-".length, -"-lightness".length);
      addPart(key, "l");
    }
  });

  return components;
};

/**
 * Converts complete HSL component groups into hex colors.
 */
const getPaletteByName = (
  components: Record<string, ColorComponents>,
): Record<string, string> => {
  const paletteByName: Record<string, string> = {};

  Object.entries(components).forEach(([name, parts]) => {
    if (
      parts.h !== undefined &&
      parts.s !== undefined &&
      parts.l !== undefined
    ) {
      paletteByName[name] = hslToHex(parts.h, parts.s, parts.l);
    }
  });

  return paletteByName;
};

/**
 * Stable base palette ordered by color name.
 */
const getSortedBasePalette = (
  paletteByName: Record<string, string>,
): string[] => {
  return Object.keys(paletteByName)
    .sort()
    .map((key) => paletteByName[key]);
};

const getHue = (color: string): number => {
  const hue = chroma(color).get("hsl.h");

  // chroma can return NaN for grayscale colors.
  // Push those to the end of hue-only sorting.
  return Number.isFinite(hue) ? hue : 360;
};

const normalizeHex = (color: string): string => color.toUpperCase();

const uniqueColors = (colors: string[]): string[] => {
  return [...new Set(colors.map(normalizeHex))];
};

/**
 * Sorts colors around the hue wheel.
 *
 * Note:
 * Red is around 0 degrees and pink/magenta is around 340 degrees,
 * so they may appear at opposite ends of this sorted array even though
 * they feel visually close.
 */
const sortByHue = (colors: string[]): string[] => {
  return [...colors].sort((a, b) => getHue(a) - getHue(b));
};

/**
 * Removes colors that are too light to be useful for charts/badges/etc.
 */
const filterLightColors = (colors: string[]): string[] => {
  return colors.filter((color) => chroma(color).get("hsl.l") < MAX_LIGHTNESS);
};

/**
 * Creates extra colors from the base palette.
 *
 * LCH gives a more perceptually balanced interpolation than RGB/HSL.
 */
const scaleColors = (baseColors: string[]): string[] => {
  const sortedBaseColors = sortByHue(baseColors);

  return chroma
    .scale(sortedBaseColors)
    .mode("lch")
    .colors(TARGET_DYNAMIC_COLOR_COUNT)
    .map(normalizeHex);
};

/**
 * Reorders hue-sorted colors to maximize separation between neighbors.
 *
 * Instead of returning:
 *   red, orange, yellow, green, blue, purple, pink
 *
 * This jumps around the hue wheel:
 *   red, blue, orange, purple, yellow, pink, green...
 *
 * The goal is not a smooth gradient. The goal is that adjacent colors
 * in the returned palette are as visually different as reasonably possible.
 */
const maximizeHueSeparation = (colors: string[]): string[] => {
  const sortedColors = sortByHue(uniqueColors(colors));

  if (sortedColors.length <= 2) {
    return sortedColors;
  }

  const result: string[] = [];
  const usedIndexes = new Set<number>();

  // Halfway around the sorted list usually gives strong separation.
  const step = Math.floor(sortedColors.length / 2);

  let index = 0;

  while (result.length < sortedColors.length) {
    // If we hit an already-used index, move forward until we find an open one.
    while (usedIndexes.has(index)) {
      index = (index + 1) % sortedColors.length;
    }

    result.push(sortedColors[index]);
    usedIndexes.add(index);

    index = (index + step) % sortedColors.length;
  }

  return result;
};

const createChromaPalette = ({
  baseColors,
}: {
  baseColors: string[];
}): string[] => {
  const scaledColors = scaleColors(baseColors);
  const filteredColors = filterLightColors(scaledColors);
  const separatedColors = maximizeHueSeparation(filteredColors);

  return separatedColors.slice(0, TARGET_DYNAMIC_COLOR_COUNT);
};

const useSocietyColorPalette = (dynamic: boolean): SocietyColorPalette => {
  const { theme } = useSocietySession();

  return useMemo(() => {
    if (!theme || !Array.isArray(theme)) {
      return { paletteByName: {}, paletteList: [] };
    }

    const components = getColorComponentsFromTheme(theme);
    const paletteByName = getPaletteByName(components);
    const sortedBasePalette = getSortedBasePalette(paletteByName);

    const societyBaseHex = paletteByName.society;

    const paletteList =
      dynamic && societyBaseHex
        ? createChromaPalette({
            baseColors: sortedBasePalette,
          })
        : sortedBasePalette;

    return { paletteByName, paletteList };
  }, [dynamic, theme]);
};

export { useSocietyColorPalette };
